import { cn } from '../../utils/cn.js';
import { TONE_COLOR } from './chart-colors.js';

export interface DivergingBar {
  key: string;
  label: string;
  /** What was lost, as a positive or negative amount (drawn left of the centre, red). */
  loss: number;
  /** What was gained (drawn right of the centre, green). */
  gain: number;
  /** The net, printed at the end in its sign's colour. */
  net: number;
  /** The printed net (default: the net to 2 decimals). */
  display?: string;
}

/**
 * Gains against losses per row (TradingView's Strategy Tester "Profits and losses"; NextAdmin traffic-sources list):
 * each row's gross loss grows left of a centre line in red, its gross profit right in green, on one scale for all rows,
 * with the net printed at the end.
 */
export function DivergingBars({ bars, title }: { bars: readonly DivergingBar[]; title: string }) {
  const max = Math.max(1e-12, ...bars.flatMap((bar) => [Math.abs(bar.loss), Math.abs(bar.gain)]));
  return (
    <ul aria-label={title} className="space-y-3">
      {bars.map((bar) => (
        <li
          key={bar.key}
          className="grid grid-cols-[minmax(4rem,8rem)_1fr] items-center gap-x-3 gap-y-1 text-sm sm:grid-cols-[minmax(4rem,9rem)_1fr_auto]"
        >
          <span className="truncate text-text-secondary" title={bar.label}>
            {bar.label}
          </span>
          <div
            className="relative h-2 overflow-hidden rounded-full bg-surface-alt"
            role="img"
            aria-label={`${bar.label}: lost ${Math.abs(bar.loss).toFixed(2)}, gained ${Math.abs(bar.gain).toFixed(2)}`}
          >
            <span className="absolute inset-y-0 left-1/2 w-px bg-faint" />
            <span
              className="absolute inset-y-0 rounded-l-full"
              style={{
                right: '50%',
                width: `${(Math.abs(bar.loss) / max) * 50}%`,
                backgroundColor: TONE_COLOR.danger,
              }}
            />
            <span
              className="absolute inset-y-0 rounded-r-full"
              style={{ left: '50%', width: `${(Math.abs(bar.gain) / max) * 50}%`, backgroundColor: TONE_COLOR.success }}
            />
          </div>
          <span
            className={cn(
              'col-span-2 font-medium tabular-nums sm:col-span-1 sm:text-right',
              bar.net < 0 ? 'text-bear' : bar.net > 0 ? 'text-bull' : 'text-muted',
            )}
          >
            {bar.display ?? bar.net.toFixed(2)}
          </span>
        </li>
      ))}
    </ul>
  );
}
